// src/utils/questionnaireRuntime.js

/* ------------------------------------------------------------------ */
/* Dynamic loader                                                  */
/* ------------------------------------------------------------------ */
/**
 * loadQuestionnaire
 * -----------------
 * Dynamically imports the question set that matches the supplied slug.
 *
 * Calling `loadQuestionnaire("pdx")` will lazy-load
 * `src/utils/questions/pdx.js` as a separate Webpack chunk so the host
 * bundle stays lean.  Every questionnaire therefore ships as its own
 * async payload.
 *
 * @param {string} name  – Slug of the questionnaire to load (e.g. "pdx").
 * @returns {Promise<object[]>}  Resolved array of question objects (empty on 404).
 */
export const loadQuestionnaire = async (name) => {
  const mod = await import(
    /* webpackChunkName: "questionnaire-[request]" */
    `./questions/${name}.js`
  );
  return mod.default || [];
};

/* ------------------------------------------------------------------ */
/* Condition filter                                                */
/* ------------------------------------------------------------------ */
/**
 * filterQuestions
 * ---------------
 * Filters the raw question list so the caller only displays questions
 * whose optional `condition` callback returns `true` for the current
 * form state / user.
 *
 * Supports both of these signatures in a question definition:
 *   • `condition(formData)`
 *   • `condition(formData, user)`
 *
 * @param {object[]} questions  – Original array returned by `loadQuestionnaire()`.
 * @param {object}   formData   – Partial answers keyed by question id.
 * @param {object}   user       – Current Firebase user (or `null`).
 * @returns {object[]}          – Sub-set of `questions` to actually render.
 */
export const filterQuestions = (questions, formData, user) =>
  questions.filter((q) => {
    if (typeof q.condition === "function") {
      return q.condition.length === 1
        ? q.condition(formData)
        : q.condition(formData, user);
    }
    return true;
  });

/* ------------------------------------------------------------------ */
/* Step builder                                                    */
/* ------------------------------------------------------------------ */
/**
 * buildSteps
 * ----------
 * Converts a flat list of questions into a wizard-friendly array of
 * “steps”.  Consecutive questions that share the same `group` name are
 * merged into a single group step; everything else becomes an individual
 * single-question step.  A final `review` step is always appended.
 *
 * Example output:
 * ```js
 * [
 *   { type: "single", question: { … } },
 *   { type: "group",  questions: [ …, … ] },
 *   { type: "review" }
 * ]
 * ```
 *
 * @param {object[]} questions  – (Possibly filtered) questions array.
 * @returns {Array<{type:string,question?:object,questions?:object[]}>}
 */
export const buildSteps = (questions) => {
  const steps = [];
  for (let i = 0; i < questions.length; ) {
    const q = questions[i];

    // ---------- Grouped questions ----------
    if (q.group) {
      const groupQs = [q];
      let j = i + 1;
      while (j < questions.length && questions[j].group === q.group) {
        groupQs.push(questions[j]);
        j++;
      }
      steps.push({ type: "group", questions: groupQs });
      i = j;
      continue;
    }

    // ---------- Single question ----------
    steps.push({ type: "single", question: q });
    i++;
  }
  steps.push({ type: "review" });
  return steps;
};

/* ------------------------------------------------------------------ */
/* Prefill range defaults                                          */
/* ------------------------------------------------------------------ */
/**
 * prefillRangeDefaults
 * --------------------
 * Scans for unanswered **range** questions and returns an object whose
 * keys are the missing question ids and whose values are the minimum
 * allowed value for that slider.  Merge this into the live `formData`
 * state so the UI reflects sensible defaults before the user makes any
 * changes.
 *
 * ```js
 * const defaults = prefillRangeDefaults(questions, formData);
 * setFormData((prev) => ({ …prev, …defaults }));
 * ```
 *
 * @param {object[]} questions  – Full question list (or the active subset).
 * @param {object}   formData   – Current answers keyed by question id.
 * @returns {object}            – Map of `{ [id]: defaultValue }`.
 */
export const prefillRangeDefaults = (questions, formData) => {
  const defaults = {};

  questions.forEach((q) => {
    const unanswered = formData[q.id] == null || formData[q.id] === "";
    if (q.fieldType === "range" && unanswered) {
      defaults[q.id] = q.min;
    }
  });

  return defaults;
};
